'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function ModernMinimalistHomepage() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-white text-gray-800 font-sans tracking-wide">
      <header className="p-6 flex justify-between items-center relative bg-white/80 backdrop-blur-sm z-10 border-b">
        <a href="#" className="w-8 h-px bg-gray-400">
            <span className="sr-only">Home</span>
        </a>
        <a href="#" className="font-semibold text-lg tracking-widest uppercase">
          object
        </a>
        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden z-20">
            {isMenuOpen ? <X/> : <Menu/>}
        </button>
        <div className={`fixed inset-0 bg-white z-10 md:hidden transform ${isMenuOpen ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 ease-in-out`}>
            <nav className="flex flex-col items-center justify-center h-full gap-8 text-2xl uppercase tracking-widest">
                <a href="#" onClick={() => setIsMenuOpen(false)}>Projects</a>
                <a href="#" onClick={() => setIsMenuOpen(false)}>Studio</a>
                <a href="#" onClick={() => setIsMenuOpen(false)}>Journal</a>
                <a href="#" onClick={() => setIsMenuOpen(false)}>Contact</a>
            </nav>
        </div>
        <nav className="hidden md:flex gap-6 text-sm uppercase tracking-wider">
            <a href="#">Projects</a>
            <a href="#">Studio</a>
            <a href="#">Journal</a>
        </nav>
      </header>
      <main className="p-6 md:p-12">
        <section className="grid md:grid-cols-2 gap-12 items-center min-h-[80vh]">
          <div className="md:order-2">
            <Image
              src="https://picsum.photos/seed/min2/800/1000"
              alt="Abstract architectural detail with clean lines and shadow play"
              width={800}
              height={1000}
              className="w-full h-auto"
              priority
              data-ai-hint="architectural detail"
            />
          </div>
          <div className="md:order-1">
            <h1 className="text-5xl md:text-7xl font-light leading-none">
              Design,
              <br />
              Distilled.
            </h1>
            <p className="mt-6 text-gray-500 max-w-sm">
              We create intentional spaces and products that balance form and
              function, beauty and utility.
            </p>
            <Button variant="link" className="mt-6 p-0 text-gray-800 group">
              View Projects{' '}
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </section>

        <section className="py-24 border-t">
          <div className="container mx-auto">
            <h2 className="text-center font-light text-3xl mb-16 tracking-wider">
              Featured Work
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="group">
                <Image
                  src="https://picsum.photos/seed/minimal-1/800/600"
                  alt="Minimalist interior with a single chair"
                  width={800}
                  height={600}
                  className="w-full h-auto transition-opacity group-hover:opacity-80"
                  data-ai-hint="minimalist interior"
                />
                <p className="mt-2 text-sm text-gray-500">The Lake House</p>
              </div>
              <div className="group">
                <Image
                  src="https://picsum.photos/seed/minimal-2/800/600"
                  alt="A single ceramic vase on a wooden table"
                  width={800}
                  height={600}
                  className="w-full h-auto transition-opacity group-hover:opacity-80"
                  data-ai-hint="ceramic vase"
                />
                <p className="mt-2 text-sm text-gray-500">Ceramics Vol. I</p>
              </div>
              <div className="group">
                <Image
                  src="https://picsum.photos/seed/minimal-3/800/600"
                  alt="A concrete building with harsh shadows"
                  width={800}
                  height={600}
                  className="w-full h-auto transition-opacity group-hover:opacity-80"
                  data-ai-hint="concrete building"
                />
                <p className="mt-2 text-sm text-gray-500">SOHO Office</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 border-t">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-center font-light text-3xl mb-16 tracking-wider">
              Our Process
            </h2>
            <div className="grid md:grid-cols-3 gap-12 text-left">
              <div>
                <div className="flex items-baseline gap-4">
                  <span className="font-light text-5xl text-gray-300">01</span>
                  <h3 className="font-semibold text-xl">Discovery</h3>
                </div>
                <p className="mt-2 text-gray-500 border-t pt-4">
                  We begin by listening. Understanding your vision, goals, and
                  constraints is the foundation of our work.
                </p>
              </div>
              <div>
                <div className="flex items-baseline gap-4">
                  <span className="font-light text-5xl text-gray-300">02</span>
                  <h3 className="font-semibold text-xl">Ideation</h3>
                </div>
                <p className="mt-2 text-gray-500 border-t pt-4">
                  Through sketches, models, and dialogue, we explore concepts
                  and refine the most promising directions.
                </p>
              </div>
              <div>
                <div className="flex items-baseline gap-4">
                  <span className="font-light text-5xl text-gray-300">03</span>
                  <h3 className="font-semibold text-xl">Execution</h3>
                </div>
                <p className="mt-2 text-gray-500 border-t pt-4">
                  With rigorous attention to detail, we bring the design to
                  life, ensuring every element is considered and crafted.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gray-50">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="font-light text-3xl mb-4 tracking-wider">
              Our Philosophy
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              "Perfection is achieved, not when there is nothing more to add,
              but when there is nothing left to take away."
            </p>
            <p className="text-sm text-gray-400 mt-2">
              - Antoine de Saint-Exupéry
            </p>
          </div>
        </section>
      </main>
      <footer className="border-t">
        <div className="container mx-auto p-12 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 gap-4">
          <span>&copy; {new Date().getFullYear()} Object Studio</span>
          <nav className="flex gap-6">
            <a href="#" className="hover:text-gray-900">
              Projects
            </a>
            <a href="#" className="hover:text-gray-900">
              Journal
            </a>
            <a href="#" className="hover:text-gray-900">
              Contact
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
